'use client';

import { Heading } from '@app/app/_components/Heading/Heading';
import { RadioButtonInput } from '@app/app/_components/Input/RadioButtonInput';
import { Select } from '@app/app/_components/Input/Select/Select';
import { Criterion } from '@app/domain/model/eshop/transport/Criterion';
import { CriterionOption } from '@app/domain/model/eshop/transport/CriterionOption';
import { CriterionSubgroupCode } from '@app/domain/model/eshop/transport/CriterionSubgroupCode';
import { CommonType } from '@app/domain/model/CommonType';
import { FormState } from '@app/lib/form/contracts/FormState';
import { PurchaseTransportInsuranceResult } from '@app/service/eshop/transport/actions/purchaseTransportInsurance';
import { useTranslations } from 'next-intl';
import { useCallback, useMemo, useState, ChangeEvent } from 'react';
import { ProductDataCriterion } from '@app/domain/model/eshop/transport/ProductDataCriterion';
import { InformationPopover } from '@app/app/_components/InformationPopover/InformationPopover';
import { QuestionCircleIcon } from '@app/app/_icons/QuestionCircleIcon';
import { ReadOnlyField } from '@app/app/_components/ReadOnlyField/ReadOnlyField';
import { useCriterionSubgroups } from '@app/app/esales/transport/_sections/setups/useCriterionSubgroups';

interface Props {
    criterions: ProductDataCriterion[];
    formState: FormState<PurchaseTransportInsuranceResult>;
    isCompany: boolean;
}

const getInitialDriverOption = (formState: FormState<PurchaseTransportInsuranceResult>, isCompany: boolean): string => {
    if (isCompany) {
        return Criterion.Default;
    }

    const savedOwnerAge = formState.data?.formData.ownerAge;
    const savedExperience = formState.data?.formData.experienceOtherDrivers;

    if (savedOwnerAge || savedExperience) {
        return CommonType.Yes;
    }
    return CommonType.No;
};

const getInitialOwnerAge = (
    formState: FormState<PurchaseTransportInsuranceResult>,
    isCompany: boolean,
    defaults: { ownerAge: CriterionOption | undefined; experienceOtherDrivers: CriterionOption | undefined },
    findOption: (subgroupCode: CriterionSubgroupCode, code: string) => CriterionOption | null,
): Nullable<CriterionOption> => {
    if (isCompany) {
        return defaults.ownerAge || null;
    }

    const savedOwnerAge = formState.data?.formData.ownerAge;
    if (savedOwnerAge) {
        const found = findOption(CriterionSubgroupCode.OwnerAge, savedOwnerAge);
        if (found) return found;
    }
    return findOption(CriterionSubgroupCode.OwnerAge, Criterion.OwnerAgeDefault);
};

const getInitialExperienceOtherDrivers = (
    formState: FormState<PurchaseTransportInsuranceResult>,
    isCompany: boolean,
    defaults: { ownerAge: CriterionOption | undefined; experienceOtherDrivers: CriterionOption | undefined },
    findOption: (subgroupCode: CriterionSubgroupCode, code: string) => CriterionOption | null,
): Nullable<CriterionOption> => {
    if (isCompany) {
        return defaults.experienceOtherDrivers || null;
    }

    const savedExperience = formState.data?.formData.experienceOtherDrivers;
    if (savedExperience) {
        const found = findOption(CriterionSubgroupCode.ExperienceOtherDrivers, savedExperience);
        if (found) return found;
    }
    return findOption(CriterionSubgroupCode.ExperienceOtherDrivers, Criterion.ExperienceOtherDriversDefault);
};

export function OtherDriversPanel({ criterions, formState, isCompany }: Props) {
    const t = useTranslations();
    const { subgroups, defaults, options, findOption } = useCriterionSubgroups(criterions);

    const [selectedDriverOption, setSelectedDriverOption] = useState<string>(
        getInitialDriverOption(formState, isCompany),
    );

    const [ownerAge, setOwnerAge] = useState<Nullable<CriterionOption>>(
        getInitialOwnerAge(formState, isCompany, defaults, findOption),
    );

    const [experienceOtherDrivers, setExperienceOtherDrivers] = useState<Nullable<CriterionOption>>(
        getInitialExperienceOtherDrivers(formState, isCompany, defaults, findOption),
    );

    const ownerAgeError = formState.violations?.ownerAge;
    const experienceOtherDriversError = formState.violations?.experienceOtherDrivers;

    const onChangeDriverOption = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            const option = e.target.value;
            setSelectedDriverOption(option);

            if (option === CommonType.No) {
                setOwnerAge(findOption(CriterionSubgroupCode.OwnerAge, Criterion.OwnerAgeDefault));
                setExperienceOtherDrivers(
                    findOption(CriterionSubgroupCode.ExperienceOtherDrivers, Criterion.ExperienceOtherDriversDefault),
                );
            } else if (option === Criterion.Default) {
                setOwnerAge(defaults.ownerAge || null);
                setExperienceOtherDrivers(defaults.experienceOtherDrivers || null);
            }
        },
        [findOption, defaults],
    );

    const onChangeOwnerAge = useCallback(
        (value: string) => {
            const found = findOption(CriterionSubgroupCode.OwnerAge, value);
            setOwnerAge(found);
        },
        [findOption],
    );

    const onChangeExperienceOtherDrivers = useCallback(
        (value: string) => {
            const found = findOption(CriterionSubgroupCode.ExperienceOtherDrivers, value);
            setExperienceOtherDrivers(found);
        },
        [findOption],
    );

    const effectiveOwnerAge = useMemo(() => {
        switch (selectedDriverOption) {
            case CommonType.No:
            case CommonType.Yes:
                return ownerAge;
            case Criterion.Default:
                return defaults.ownerAge;
            default:
                return ownerAge;
        }
    }, [selectedDriverOption, ownerAge, defaults.ownerAge]);

    const effectiveExperienceOtherDrivers = useMemo(() => {
        switch (selectedDriverOption) {
            case CommonType.No:
            case CommonType.Yes:
                return experienceOtherDrivers;
            case Criterion.Default:
                return defaults.experienceOtherDrivers;
            default:
                return experienceOtherDrivers;
        }
    }, [selectedDriverOption, experienceOtherDrivers, defaults.experienceOtherDrivers]);

    const otherDriversChoices = useMemo(() => {
        return [
            {
                value: CommonType.No,
                label: t('general.no'),
            },
            {
                value: CommonType.Yes,
                label: t('general.yes'),
            },
        ];
    }, [t]);

    const showDetailSelects = selectedDriverOption === CommonType.Yes;

    return (
        <div className="flex flex-col gap-4 px-6">
            <input type="hidden" name="ownerAge" value={effectiveOwnerAge?.code || ''} />
            <input type="hidden" name="experienceOtherDrivers" value={effectiveExperienceOtherDrivers?.code || ''} />

            <div className="flex flex-row items-center gap-2">
                <Heading level="h3">{t('eshop.transport.otherDrivers')}</Heading>
                <InformationPopover content={t('eshop.transport.otherDriversTooltip')}>
                    <QuestionCircleIcon className="size-5 text-neutral-500" />
                </InformationPopover>
            </div>

            {isCompany ? (
                <div className="flex flex-col gap-4">
                    <ReadOnlyField label={subgroups.ownerAge?.criterionSubgroupName || t('eshop.transport.driverAge')}>
                        {defaults.ownerAge?.name}
                    </ReadOnlyField>
                    <ReadOnlyField
                        label={
                            subgroups.experienceOtherDrivers?.criterionSubgroupName ||
                            t('eshop.transport.experienceOfOtherDrivers')
                        }>
                        {defaults.experienceOtherDrivers?.name}
                    </ReadOnlyField>
                </div>
            ) : (
                <>
                    <RadioButtonInput
                        name="otherDriversChoice"
                        defaultValue={selectedDriverOption}
                        onChange={onChangeDriverOption}
                        choices={otherDriversChoices}
                    />

                    {showDetailSelects && (
                        <div className="flex flex-col gap-4">
                            <Select
                                options={options.ownerAge}
                                searchable
                                label={t('eshop.transport.driverAge')}
                                name="ownerAgeSelect"
                                placeholder={t('eshop.general.select')}
                                value={ownerAge?.code || ''}
                                onChange={onChangeOwnerAge}
                                error={ownerAgeError}
                            />
                            <Select
                                options={options.experienceOtherDrivers}
                                searchable
                                label={t('eshop.transport.experienceOfOtherDrivers')}
                                name="experienceOtherDriversSelect"
                                placeholder={t('eshop.general.select')}
                                value={experienceOtherDrivers?.code || ''}
                                onChange={onChangeExperienceOtherDrivers}
                                error={experienceOtherDriversError}
                            />
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
